import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import Slider, { CustomArrowProps } from "react-slick";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "../../Utils/assets/productCarousel.scss";
import { getImage } from "../../API/Products.api";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";
import IfcViewer from "../../Pages/ifcViewer";

type ProductCarouselProps = {
  imageIds: string[];
  data: any
};

export const ProductCarousel = (props: ProductCarouselProps) => {
  const [mainSliderNav, setMainSliderNav] = useState<Slider | null>(null);
  const [navSliderNav, setNavSliderNav] = useState<Slider | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [resizedUrls, setResizedUrls] = useState<string[]>([]);
  const [productDetails, setProductDetails] = useState<any>([]);
  const [showIfcViewer, setShowIfcViewer] = useState<boolean>();

  useEffect(() => {
    if (props.imageIds.length > 0) {
      getImageUrls(props.imageIds);
    } else {
      setImageUrls(["/assets/imgs/dummy-img.png"]);
      setResizedUrls(["/assets/imgs/dummy-img.png"]);
    }
  }, [props.imageIds]);

  useEffect(() => {
    const ifcFiles = props.data.fileDescriptors.filter((file: any) =>
      file.fileName?.toLowerCase().endsWith(".ifc")
    );
    if (ifcFiles.length > 0) {
      setShowIfcViewer(true)
    } else {
      setShowIfcViewer(false)
    }
    setProductDetails(props.data)
  }, [props.data])

  // resize to fixed width & height
  const resizeImage = (
    url: string,
    maxWidth: number,
    maxHeight: number
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          // console.log(
          //   `📷 Original: ${img.width}x${img.height} → Resized: ${maxWidth}x${maxHeight}`
          // );

          const canvas = document.createElement("canvas");
          canvas.width = maxWidth;
          canvas.height = maxHeight;

          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, maxWidth, maxHeight);

          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = (err) => reject(err);
      img.src = url;
    });
  };


  // Fetch images and prepare both arrays
  const getImageUrls = async (imageIds: string[]) => {
    let originals: string[] = [];
    let thumbnails: string[] = [];

    for (let i = 0; i < imageIds.length; i++) {
      const url = await getImage(imageIds[i], "hq");
      if (url) {
        originals.push(url);

        // create resized thumbnail
        const resized = await resizeImage(url, 500, 300);
        thumbnails.push(resized);
      } else {
        originals.push("../assets/imgs/dummy-img.png");
        thumbnails.push("../assets/imgs/dummy-img.png");
      }
    }

    setImageUrls(originals); // full-resolution
    setResizedUrls(thumbnails); // thumbnails
  };

  const NextArrow = ({ currentSlide = 0, slideCount = 0, ...props }: CustomArrowProps) => {
    const disabled = currentSlide === slideCount - 1;
    return (
      <div {...props} style={{
        color: disabled ? "rgba(180, 179, 179, 0.73)" : "rgb(112, 112, 112)",
        cursor: disabled ? "not-allowed" : "pointer"
      }}>
        <ArrowForwardIosIcon className="next-slide" />
      </div>
    );
  };

  const PreviousArrow = ({ currentSlide = 0, ...props }: CustomArrowProps) => {
    const disabled = currentSlide === 0;
    return (
      <div {...props} style={{
        color: disabled ? "rgba(112, 112, 112, 0.3)" : "rgb(112, 112, 112)",
        cursor: disabled ? "not-allowed" : "pointer"
      }}>
        <ArrowBackIosIcon className="prev-slide" />
      </div>
    );
  };


  //Main carousel slider settings
  const settingsMainSlider = {
    className: "main-slider",
    initialSlide: 0,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    nextArrow: <NextArrow />,
    prevArrow: <PreviousArrow />,
  };

  //Navigation slider settings
  const settingsNavSlider = {
    className: "nav-slider",
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: true,
    focusOnSelect: true,
    vertical: true,
    verticalSwiping: true,
    responsive: [
      {
        breakpoint: 768, // <=768px
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 992, // <=992px
        settings: { slidesToShow: 3 },
      },
      {
        breakpoint: 1200, // <=1200
        settings: { slidesToShow: 3 },
      },
      {
        breakpoint: 1600, // <=1600
        settings: { slidesToShow: 3 },
      },
      {
        breakpoint: 1640, // <=1640px
        settings: { slidesToShow: 4 },
      },
    ]
  };

  if (imageUrls.length > 0) {
    return (
      <Box className="product-slider" id="img">
        {showIfcViewer ? <IfcViewer productDetails={productDetails} /> : null}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
          <Slider
            {...settingsMainSlider}
            asNavFor={navSliderNav ?? undefined}
            ref={(mainSlider: Slider) => setMainSliderNav(mainSlider)}
            nextArrow={<NextArrow />}
            prevArrow={<PreviousArrow />}
          >
            {resizedUrls.map((thumb, index) => (
              <div key={index}>
                <InnerImageZoom src={thumb} zoomType="hover" hideHint fullscreenOnMobile zoomPreload zoomScale={3} />
              </div>
            ))}
          </Slider>

          <Slider
            {...settingsNavSlider}
            asNavFor={mainSliderNav ? mainSliderNav : undefined}
            ref={(navSlider: Slider) => setNavSliderNav(navSlider)}
          >
            {resizedUrls.map((thumb: string, index: number) => (
              <div key={index}>
                <img
                  src={thumb}
                  alt={"carousel image " + index}
                  style={{ height: "100%", objectFit: "cover" }}
                  // className="img-responsive"
                  id={"image" + index}
                  loading="lazy"
                />
              </div>
            ))}
          </Slider>
        </div>
      </Box>
    );
  }

  return <></>;
};
